// ContractCanvas engine — freelancer contract templates.

export interface ContractParty {
  name: string;
  address: string;
  email: string;
  phone: string;
  signatureName: string;
  signedISO?: string;
}

export interface ContractData {
  id: string;
  templateId: string;
  title: string;
  jurisdiction: string;
  effectiveDate: string;
  contractor: ContractParty;
  client: ContractParty;
  scopeSummary: string;
  deliverables: string[];
  paymentTerms: {
    totalPrice: number;
    currency: string;
    schedule: 'on_completion' | 'milestones' | '50_50' | 'monthly_retainer' | 'hourly';
    depositPercent: number;
    netDays: number;
    lateFeePercent: number;
  };
  ipOwnership: 'client_full' | 'client_on_payment' | 'shared' | 'contractor_keeps_rights';
  termWeeks: number;
  terminationNoticeDays: number;
  noncompeteMonths: number;
  confidentialityMonths: number;
  notes: string;
  status: 'draft' | 'sent' | 'signed' | 'completed' | 'terminated';
}

export const TEMPLATES = {
  consulting_services: {
    id: 'consulting_services',
    name: 'Consulting Services',
    defaults: { ipOwnership: 'client_on_payment' as const, schedule: '50_50' as const, netDays: 14, depositPercent: 50, termWeeks: 12, terminationNoticeDays: 14, lateFeePercent: 1.5 },
  },
  design_dev: {
    id: 'design_dev',
    name: 'Design & Development',
    defaults: { ipOwnership: 'client_on_payment' as const, schedule: 'milestones' as const, netDays: 14, depositPercent: 33, termWeeks: 16, terminationNoticeDays: 14, lateFeePercent: 1.5 },
  },
  monthly_retainer: {
    id: 'monthly_retainer',
    name: 'Monthly Retainer',
    defaults: { ipOwnership: 'client_full' as const, schedule: 'monthly_retainer' as const, netDays: 30, depositPercent: 100, termWeeks: 52, terminationNoticeDays: 30, lateFeePercent: 1.0 },
  },
  hourly_advisory: {
    id: 'hourly_advisory',
    name: 'Hourly Advisory',
    defaults: { ipOwnership: 'contractor_keeps_rights' as const, schedule: 'hourly' as const, netDays: 14, depositPercent: 0, termWeeks: 26, terminationNoticeDays: 7, lateFeePercent: 1.5 },
  },
};

export function emptyContract(): ContractData {
  return {
    id: crypto.randomUUID(),
    templateId: 'consulting_services',
    title: '',
    jurisdiction: 'Ontario, Canada',
    effectiveDate: new Date().toISOString().slice(0, 10),
    contractor: { name: '', address: '', email: '', phone: '', signatureName: '' },
    client: { name: '', address: '', email: '', phone: '', signatureName: '' },
    scopeSummary: '',
    deliverables: [],
    paymentTerms: { totalPrice: 0, currency: 'USD', schedule: '50_50', depositPercent: 50, netDays: 14, lateFeePercent: 1.5 },
    ipOwnership: 'client_on_payment',
    termWeeks: 12,
    terminationNoticeDays: 14,
    noncompeteMonths: 0,
    confidentialityMonths: 24,
    notes: '',
    status: 'draft',
  };
}

export function applyTemplate(contract: ContractData, templateId: string): ContractData {
  const tpl = TEMPLATES[templateId as keyof typeof TEMPLATES];
  if (!tpl) return contract;
  return {
    ...contract,
    templateId,
    ipOwnership: tpl.defaults.ipOwnership,
    termWeeks: tpl.defaults.termWeeks,
    terminationNoticeDays: tpl.defaults.terminationNoticeDays,
    paymentTerms: {
      ...contract.paymentTerms,
      schedule: tpl.defaults.schedule,
      depositPercent: tpl.defaults.depositPercent,
      netDays: tpl.defaults.netDays,
      lateFeePercent: tpl.defaults.lateFeePercent,
    },
  };
}

function ipText(c: ContractData): string {
  switch (c.ipOwnership) {
    case 'client_full': return 'All intellectual property created under this Agreement vests in Client immediately upon creation.';
    case 'client_on_payment': return 'Intellectual property in deliverables transfers to Client upon receipt of full payment. Until then, Contractor retains all rights.';
    case 'shared': return 'Intellectual property in deliverables is jointly owned by Client and Contractor. Either party may use the work product without restriction or royalty.';
    case 'contractor_keeps_rights': return 'Contractor retains all intellectual property rights in deliverables. Client receives a perpetual, worldwide, royalty-free, non-exclusive license to use the deliverables for its internal business purposes.';
  }
}

function scheduleText(c: ContractData): string {
  const cur = c.paymentTerms.currency; const tot = c.paymentTerms.totalPrice.toLocaleString();
  switch (c.paymentTerms.schedule) {
    case 'on_completion': return `Client shall pay Contractor ${cur} ${tot} upon completion and acceptance of all deliverables.`;
    case 'milestones': return `Client shall pay Contractor a total of ${cur} ${tot}, invoiced at agreed milestones. A non-refundable deposit of ${c.paymentTerms.depositPercent}% (${cur} ${(c.paymentTerms.totalPrice * c.paymentTerms.depositPercent / 100).toLocaleString()}) is due upon execution of this Agreement.`;
    case '50_50': return `Client shall pay Contractor a total of ${cur} ${tot} in two installments: ${c.paymentTerms.depositPercent}% (${cur} ${(c.paymentTerms.totalPrice * c.paymentTerms.depositPercent / 100).toLocaleString()}) on execution and the balance on delivery.`;
    case 'monthly_retainer': return `Client shall pay Contractor ${cur} ${tot} per month, invoiced on the first business day of each month.`;
    case 'hourly': return `Client shall pay Contractor an hourly rate as set out in Schedule A, invoiced bi-weekly. The estimated engagement value is ${cur} ${tot}.`;
  }
}

export function generateContract(c: ContractData): string {
  return `# ${c.title || 'Independent Contractor Agreement'}

**Effective Date:** ${c.effectiveDate}
**Governing law:** ${c.jurisdiction}

This Agreement is entered into between:

**Contractor:** ${c.contractor.name}
${c.contractor.address}
${c.contractor.email} · ${c.contractor.phone}

**Client:** ${c.client.name}
${c.client.address}
${c.client.email} · ${c.client.phone}

## 1. Services

${c.scopeSummary || '_(scope of work to be attached as Schedule A)_'}

### 1.1 Deliverables
${c.deliverables.length ? c.deliverables.map(d => `- ${d}`).join('\n') : '_(see Schedule A)_'}

## 2. Term

This Agreement shall commence on the Effective Date and continue for ${c.termWeeks} weeks, or until terminated under Section 8.

## 3. Compensation

${scheduleText(c)}

All invoices are payable net ${c.paymentTerms.netDays} days from the invoice date. Amounts unpaid past the due date shall accrue interest at ${c.paymentTerms.lateFeePercent}% per month or the maximum permitted by law, whichever is lower.

## 4. Intellectual Property

${ipText(c)}

## 5. Confidentiality

Each party shall keep confidential all non-public information disclosed by the other party for a period of ${c.confidentialityMonths} months after the end of this Agreement.

## 6. Independent Contractor

Contractor is engaged as an independent contractor and not as an employee, partner, or agent of Client. Contractor is solely responsible for its own taxes, insurance, and benefits.

${c.noncompeteMonths > 0 ? `## 7. Non-Competition

For ${c.noncompeteMonths} months after termination, Contractor shall not directly solicit Client's customers for substantially similar services. This clause is limited to the geographic and market scope of Client's actual business at the time of termination.\n` : ''}

## ${c.noncompeteMonths > 0 ? '8' : '7'}. Termination

Either party may terminate this Agreement on ${c.terminationNoticeDays} days' written notice. Upon termination, Client shall pay Contractor for all services performed up to the termination date.

## ${c.noncompeteMonths > 0 ? '9' : '8'}. Limitation of Liability

Contractor's total liability arising out of this Agreement shall not exceed the fees actually paid by Client to Contractor in the six (6) months preceding the claim. Neither party shall be liable for indirect, special, or consequential damages.

## ${c.noncompeteMonths > 0 ? '10' : '9'}. Notices

All notices shall be in writing and sent to the email addresses set out above.

## ${c.noncompeteMonths > 0 ? '11' : '10'}. Entire Agreement

This Agreement (together with any attached Schedules) constitutes the entire agreement between the parties and supersedes all prior agreements on this subject.

${c.notes ? `## Additional Notes\n${c.notes}\n` : ''}

---

**SIGNATURES**

Contractor:

_____________________________  Date: ____________
${c.contractor.signatureName || c.contractor.name}

Client:

_____________________________  Date: ____________
${c.client.signatureName || c.client.name}

---

_This template is provided for informational purposes and is not legal advice. Consult a lawyer in your jurisdiction before executing._
`;
}
